/**
 * <text-show-more>
 * - Shows the toggle link only when the visible text span (desktop OR mobile) is actually clamped.
 * - Expands / collapses the text with a smooth height animation.
 * - Toggles the label between "Mehr anzeigen" and "Weniger anzeigen".
 * Labels can be overridden via data-label-more / data-label-less on <text-show-more>.
 */
if (!customElements.get('text-show-more')) {
  class TextShowMore extends HTMLElement {
    #resizeObserver;
    #animating = false;

    connectedCallback() {
      this.block = this.closest('.text-block');
      this.button = this.querySelector('button');
      if (!this.block || !this.button) return;

      this.texts = this.block.querySelectorAll(':scope > .text-block__desktop, :scope > .text-block__mobile');
      this.labelMore = this.dataset.labelMore || 'Mehr anzeigen';
      this.labelLess = this.dataset.labelLess || 'Weniger anzeigen';

      this.button.addEventListener('click', this.#toggle);
      this.#resizeObserver = new ResizeObserver(this.#update);
      this.texts.forEach((el) => this.#resizeObserver.observe(el));
      this.#update();
    }

    disconnectedCallback() {
      this.#resizeObserver?.disconnect();
      this.button?.removeEventListener('click', this.#toggle);
    }

    get #visibleText() {
      return [...this.texts].find((el) => el.getClientRects().length > 0);
    }

    get #expanded() {
      return this.block.classList.contains('is-expanded');
    }

    get #reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /** Show the link only if the text is longer than the line limit (checked in clamped state). */
    #update = () => {
      if (this.#animating) return;
      const el = this.#visibleText;
      if (!el) {
        this.hidden = true;
        return;
      }

      const wasExpanded = this.#expanded;
      if (wasExpanded) this.block.classList.remove('is-expanded');

      const clampedHeight = el.getBoundingClientRect().height;
      el.style.setProperty('-webkit-line-clamp', 'unset');
      const fullHeight = el.getBoundingClientRect().height;
      el.style.removeProperty('-webkit-line-clamp');

      if (wasExpanded) this.block.classList.add('is-expanded');

      this.hidden = !(fullHeight - clampedHeight > 1);
    };

    #toggle = () => {
      if (this.#animating) return;
      const el = this.#visibleText;
      if (!el) return;
      this.#expanded ? this.#collapse(el) : this.#expand(el);
    };

    #expand(el) {
      const from = el.getBoundingClientRect().height;
      this.block.classList.add('is-expanded');
      const to = el.getBoundingClientRect().height;
      this.#setLabel(true);
      this.#animateHeight(el, from, to);
    }

    #collapse(el) {
      const from = el.getBoundingClientRect().height;
      this.block.classList.remove('is-expanded');
      const to = el.getBoundingClientRect().height;
      this.#setLabel(false);

      // Keep the full text visible while the height shrinks, re-clamp at the end
      // so the "…" appears exactly when the animation finishes.
      this.block.classList.add('is-expanded');
      this.#animateHeight(el, from, to, () => {
        this.block.classList.remove('is-expanded');
        this.#scrollBackIfNeeded();
      });
    }

    #animateHeight(el, from, to, onDone = () => {}) {
      if (this.#reducedMotion || Math.abs(to - from) < 1 || !el.animate) {
        onDone();
        return;
      }

      this.#animating = true;
      el.style.overflow = 'hidden';

      const animation = el.animate(
        [{ height: `${from}px` }, { height: `${to}px` }],
        { duration: 350, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );

      const finish = () => {
        el.style.removeProperty('overflow');
        this.#animating = false;
        onDone();
      };
      animation.finished.then(finish, finish);
    }

    /** After collapsing a long text, bring the block back into view if its top scrolled out of sight. */
    #scrollBackIfNeeded() {
      if (this.block.getBoundingClientRect().top < 0) {
        this.block.scrollIntoView({ behavior: this.#reducedMotion ? 'auto' : 'smooth', block: 'start' });
      }
    }

    #setLabel(expanded) {
      this.button.textContent = expanded ? this.labelLess : this.labelMore;
      this.button.setAttribute('aria-expanded', String(expanded));
    }
  }

  customElements.define('text-show-more', TextShowMore);
}